import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Check, ShoppingCart } from "lucide-react";

// Per-size inventory is the single source of truth; aggregate stock only when a product
// has no per-size tracking at all. A missing size key = 0 available.
function sizeAvail(p, size) {
  const sbs = p?.stock_by_size || {};
  if (sbs && Object.keys(sbs).length > 0) return sbs[size] ?? 0;
  return p?.stock ?? 0;
}

// Self-contained Complete Rugby Kit builder. Steps: choose jersey -> jersey size ->
// shorts -> shorts size, then ADD COMPLETE KIT. Frontend validation is UX-only; the
// Phase 1 server re-pricing/stock validation remains authoritative at checkout.
export default function KitBuilder({ bundle }) {
  const { addBundle } = useCart();
  const navigate = useNavigate();
  const groups = bundle.kit_groups || [];
  const [sel, setSel] = useState(() => groups.map(() => ({ product_id: "", size: "" })));

  const groupProduct = (gi, pid) => (groups[gi]?.products || []).find((p) => p.product_id === pid);

  const chooseProduct = (gi, p) => {
    const firstSize = (p.sizes || []).find((s) => sizeAvail(p, s) > 0) || "";
    setSel((prev) => prev.map((s, i) => (i === gi ? { product_id: p.product_id, size: firstSize } : s)));
  };
  const chooseSize = (gi, size) => setSel((prev) => prev.map((s, i) => (i === gi ? { ...s, size } : s)));

  const complete = sel.length === groups.length && sel.every((s, gi) => {
    if (!s.product_id || !s.size) return false;
    const p = groupProduct(gi, s.product_id);
    return p && sizeAvail(p, s.size) > 0;
  });

  const price = Number(bundle.bundle_price || 0);
  const compareAt = Number(bundle.compare_at_price ?? bundle.max_normal_total ?? 0);
  const saving = Math.max(0, compareAt - price);

  const handleAdd = () => {
    if (!complete) { toast.error("Please choose a jersey and shorts with in-stock sizes."); return; }
    const units = sel.map((s, gi) => {
      const p = groupProduct(gi, s.product_id);
      return {
        product_id: p.product_id, name: p.name, image_url: p.image_url,
        size: s.size, normal: p.price, role_label: groups[gi].label || groups[gi].role || "",
      };
    });
    addBundle(bundle, units);
    toast.success("Complete Kit added to cart!");
    navigate("/cart");
  };

  // step index runs across all groups: group 0 -> steps 1 & 2, group 1 -> steps 3 & 4
  return (
    <div data-testid="kit-builder">
      {/* Price block */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <span data-testid="kit-price" className="text-[#F7931E] font-black text-4xl sm:text-5xl">R{price.toFixed(0)}</span>
        {compareAt > price && (
          <div className="mb-1">
            <span className="text-[#737373] line-through text-lg" data-testid="kit-was">Was R{compareAt.toFixed(0)}</span>
            <span className="ml-3 text-green-400 font-bold text-sm" data-testid="kit-saving">Save R{saving.toFixed(0)}</span>
          </div>
        )}
      </div>
      <p className="text-[#A3A3A3] text-xs font-bold uppercase tracking-[0.2em] mb-6">While stock lasts</p>

      <div className="space-y-6">
        {groups.map((g, gi) => {
          const chosen = groupProduct(gi, sel[gi]?.product_id);
          const label = g.label || g.role || `Item ${gi + 1}`;
          const prodStep = gi * 2 + 1;
          const sizeStep = gi * 2 + 2;
          return (
            <div key={gi} data-testid={`kit-group-${gi}`} className="space-y-4">
              {/* Product selection */}
              <div className="bg-[#171717] border border-[#262626] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-[#F7931E] text-black text-xs font-black shrink-0">{prodStep}</span>
                  <h3 className="text-white font-bold uppercase tracking-tight text-base font-heading">Choose Your {label}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(g.products || []).map((p) => {
                    const selected = sel[gi]?.product_id === p.product_id;
                    const soldOut = !(p.sizes || []).some((s) => sizeAvail(p, s) > 0);
                    return (
                      <button
                        key={p.product_id}
                        data-testid={`kit-product-${gi}-${p.product_id}`}
                        disabled={soldOut}
                        onClick={() => !soldOut && chooseProduct(gi, p)}
                        className={`relative text-left border transition-colors overflow-hidden group ${
                          selected ? "border-[#F7931E] ring-1 ring-[#F7931E]" : "border-[#262626] hover:border-[#F7931E]"
                        } ${soldOut ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                          {p.image_url
                            ? <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-[#525252] text-xs">No image</div>}
                          {selected && (
                            <span className="absolute top-1.5 right-1.5 w-6 h-6 bg-[#F7931E] text-black flex items-center justify-center">
                              <Check size={14} strokeWidth={3} />
                            </span>
                          )}
                          {soldOut && (
                            <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-bold uppercase">Sold Out</span>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-white text-xs font-bold leading-tight line-clamp-2">{p.name}</p>
                          <p className="text-[#A3A3A3] text-[11px] mt-0.5">R{Number(p.price).toFixed(0)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Size selection — only after a product is chosen */}
              {chosen && (
                <div className="bg-[#171717] border border-[#262626] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#F7931E] text-black text-xs font-black shrink-0">{sizeStep}</span>
                    <h3 className="text-white font-bold uppercase tracking-tight text-base font-heading">Choose {label} Size</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(chosen.sizes || []).map((sz) => {
                      const inStock = sizeAvail(chosen, sz) > 0;
                      const selected = sel[gi]?.size === sz;
                      return (
                        <button
                          key={sz}
                          data-testid={`kit-size-${gi}-${sz}`}
                          disabled={!inStock}
                          onClick={() => inStock && chooseSize(gi, sz)}
                          title={!inStock ? "Out of stock" : ""}
                          className={`min-w-[48px] px-4 py-2 text-sm font-bold border transition-colors ${
                            selected ? "bg-[#F7931E] text-black border-[#F7931E]"
                            : inStock ? "bg-black text-white border-[#262626] hover:border-[#F7931E]"
                            : "bg-black text-[#525252] border-[#1a1a1a] line-through cursor-not-allowed"
                          }`}
                        >
                          {sz}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        data-testid="add-kit-to-cart-btn"
        onClick={handleAdd}
        disabled={!complete}
        className="w-full mt-8 bg-[#F7931E] text-black font-black uppercase tracking-wide py-4 hover:bg-[#D97715] transition-colors btn-press text-base sm:text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ShoppingCart size={20} /> Add Complete Kit to Cart — R{price.toFixed(0)}
      </button>
      {!complete && (
        <p className="text-[#737373] text-xs text-center mt-3" data-testid="kit-incomplete-hint">
          Select a jersey, jersey size, shorts and shorts size to continue.
        </p>
      )}
    </div>
  );
}
